import { useMemo, useState, type FC } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useLanguagesQuery } from "../hooks/useLanguagesQuery";
import type { Language } from "../model/types";

interface LanguageAutocompleteProps {
  value: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  disabled?: boolean;
  /**
   * Уже выбранный язык (например, из запроса текущей платформы) — чтобы не
   * делать отдельный запрос "найти язык по id" только ради подписи значения,
   * выбранного до того, как пользователь начал искать.
   */
  initialLanguage?: Language | null;
}

function languageLabel(language: Language): string {
  return language.code
    ? `${language.title} (${language.code})`
    : language.title;
}

/**
 * Автокомплит выбора языка (Query.languages(search)). Плоский список, а не
 * {options,value} как у остальных автокомплитов формы, поэтому это
 * самостоятельный компонент, а не общий AutocompleteField.
 */
export const LanguageAutocomplete: FC<LanguageAutocompleteProps> = ({
  value,
  onChange,
  label,
  disabled,
  initialLanguage,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounce(inputValue, 300);

  const { data, loading } = useLanguagesQuery({
    variables: { search: debouncedSearch || undefined },
    fetchPolicy: "cache-and-network",
  });

  const options = useMemo<Language[]>(() => {
    const loaded = data?.languages ?? [];
    if (
      initialLanguage &&
      value === initialLanguage.id &&
      !loaded.some((language) => language.id === initialLanguage.id)
    ) {
      return [initialLanguage, ...loaded];
    }
    return loaded;
  }, [data, initialLanguage, value]);

  const selectedOption =
    options.find((language) => language.id === value) ||
    (value && initialLanguage?.id === value ? initialLanguage : null) ||
    null;

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_event, newValue) => onChange(newValue ? newValue.id : null)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        // reason "reset" не фильтруем: MUI приводит текст инпута к label
        // выбранной опции именно через reset-вызов — с фильтром выбранное
        // значение (в т.ч. начальное) выглядело вечно пустым.
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      loadingText={t("shared.languageAutocomplete.loading")}
      noOptionsText={t("shared.languageAutocomplete.noOptions")}
      disabled={disabled}
      getOptionLabel={languageLabel}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      filterOptions={(opts) => opts}
      renderInput={(params) => (
        <TextField {...params} label={label} fullWidth />
      )}
    />
  );
};
